// 製作尺寸：整個專案的 LED 畫布等比縮小（動畫師交縮小版，現場 D3 放大）。
// 專案永遠保留原尺寸（projects.led_canvas、版型的原始座標），只另外存一個目前用的比例（projects.work_scale，例 '1/2'；沒有 = 原尺寸）。
// 這個檔前後台共用（cf-worker 直接 import，所以只用抹掉型別就能跑的語法、不 import 別的檔）：
//   - 哪些比例能選、不能選的原因（指名哪一塊 LED / 哪個版型的哪個值、縮完會變多少）
//   - 把畫布 / 版型的座標乘上比例（輸出時統一乘；存的永遠是原尺寸）
// 條件：縮完後畫布的寬高、每一塊 LED 的 x y w h、每個版型的畫布和每塊螢幕的 x y w h 都要是「整數雙數」。
// 容許極小的浮點誤差（1374.9999999 算 1375），但不四捨五入蒙混：1374.5 就是 1374.5，不能選。

export interface WorkScale { num: number; den: number }

export interface SizeLike { width: number; height: number }
export interface RectLike { x: number; y: number; w: number; h: number }

/** 常見比例：不能選的時候拿來說明原因（第一個 = 原尺寸）。實際能選哪些看 workScalePickList：所有剛好等比、全部雙數的尺寸都列 */
export const WORK_SCALE_CANDIDATES: readonly WorkScale[] = Object.freeze([
  { num: 1, den: 1 }, { num: 3, den: 4 }, { num: 2, den: 3 }, { num: 1, den: 2 }, { num: 1, den: 3 }, { num: 1, den: 4 }, { num: 1, den: 8 },
]);

export const FULL_WORK_SCALE: Readonly<WorkScale> = Object.freeze({ num: 1, den: 1 });

/** 縮小後的畫布最小邊（跟 LED 畫布的下限一樣） */
export const MIN_WORK_SIDE = 16;

/** 浮點誤差容許：離整數這麼近就當整數（UV 算出來的 1374.9999999 算 1375） */
const EPS = 1e-6;

export const isFullScale = (s: WorkScale | null | undefined): boolean => !s || (s.num === s.den);

const gcd = (a: number, b: number): number => { a = Math.abs(a); b = Math.abs(b); while (b) { [a, b] = [b, a % b]; } return a; };

/** 分母上限（畫布最大 32768，比例的分母不會超過它） */
const MAX_DEN = 65536;

/** '1/2'、'43/44' → 約分後的比例；null / 空 / 原尺寸 / 不是小於 1 的分數 = null（= 原尺寸）。後台存的字串用這個讀 */
export function parseWorkScale(raw: unknown): WorkScale | null {
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    const o = raw as Record<string, unknown>;
    raw = `${o.num}/${o.den}`;
  }
  if (typeof raw !== 'string') return null;
  const m = /^\s*(\d{1,5})\s*\/\s*(\d{1,5})\s*$/.exec(raw);
  if (!m) return null;
  const num = Number(m[1]), den = Number(m[2]);
  if (num < 1 || num >= den || den > MAX_DEN) return null;
  const d = gcd(num, den);
  return { num: num / d, den: den / d };
}

/** 前端 / 表單送來的值是不是合法的比例（含原尺寸）：是 → 比例；不是 → undefined。null = 原尺寸。能不能用這個專案另外檢查 */
export function readWorkScaleInput(raw: unknown): WorkScale | null | undefined {
  if (raw === null || raw === undefined) return null;
  if (typeof raw === 'string' && /^\s*1\s*\/\s*1\s*$/.test(raw)) return null;
  const s = parseWorkScale(raw);
  return s ?? undefined;
}

/** 存進資料庫的字串（原尺寸 = null） */
export const workScaleToDb = (s: WorkScale | null | undefined): string | null => (isFullScale(s) ? null : `${s!.num}/${s!.den}`);

/** 畫面上的字：75%、97.73%（最多兩位小數，只給人看；存的是分數，尺寸不會有誤差） */
export const workScaleText = (s: WorkScale | null | undefined): string => (isFullScale(s) ? '原尺寸' : `${Math.round((s!.num / s!.den) * 10000) / 100}%`);

export const workScaleKey = (s: WorkScale | null | undefined): string => `${s?.num ?? 1}/${s?.den ?? 1}`;

export const sameWorkScale = (a: WorkScale | null | undefined, b: WorkScale | null | undefined): boolean =>
  (a?.num ?? 1) * (b?.den ?? 1) === (b?.num ?? 1) * (a?.den ?? 1);

// ───────────────────────── 乘比例 ─────────────────────────

/** v × 比例；離整數很近就當那個整數（不四捨五入：1374.5 還是 1374.5） */
export function scaleValue(v: number, s: WorkScale | null | undefined): number {
  if (isFullScale(s)) return v;
  const x = (v * s!.num) / s!.den;
  const r = Math.round(x);
  return Math.abs(x - r) <= EPS ? r : x;
}

export const scaleCanvasSize = <C extends SizeLike>(c: C, s: WorkScale | null | undefined): C =>
  (isFullScale(s) ? c : { ...c, width: scaleValue(c.width, s), height: scaleValue(c.height, s) } as C);

export const scaleRect = <R extends RectLike>(r: R, s: WorkScale | null | undefined): R =>
  (isFullScale(s) ? r : { ...r, x: scaleValue(r.x, s), y: scaleValue(r.y, s), w: scaleValue(r.w, s), h: scaleValue(r.h, s) } as R);

/** ledCabinet.ts 的 DEFAULT_CABINET（0.5m × 0.5m = 128 × 128 點）；這個檔不 import 別的檔，所以寫一份在這裡 */
const DEFAULT_CABINET_POINTS = { w: 128, h: 128 };

/** 縮小後，沒設箱體的 LED 一格是幾點（128 × 比例） */
export const scaledDefaultCabinet = (s: WorkScale | null | undefined): { w: number; h: number } =>
  ({ w: scaleValue(DEFAULT_CABINET_POINTS.w, s), h: scaleValue(DEFAULT_CABINET_POINTS.h, s) });

const isEvenInt = (n: number): boolean => Number.isInteger(n) && n % 2 === 0;

/** 縮完是不是「整數雙數」 */
export const scaledIsEvenInt = (v: number, s: WorkScale | null | undefined): boolean => isEvenInt(scaleValue(v, s));

/**
 * 專案的畫布縮小後的大小；縮完不是整數（資料對不上比例）→ 不縮，回原尺寸並標 fits = false，
 * 畫面不會拿到小數的畫布大小
 */
export function effectiveWorkCanvas<C extends SizeLike>(canvas: C, s: WorkScale | null | undefined): { canvas: C; fits: boolean; scale: WorkScale | null } {
  if (isFullScale(s)) return { canvas, fits: true, scale: null };
  const scaled = scaleCanvasSize(canvas, s);
  if (!Number.isInteger(scaled.width) || !Number.isInteger(scaled.height)) return { canvas, fits: false, scale: null };
  return { canvas: scaled, fits: true, scale: { num: s!.num, den: s!.den } };
}

/** 版型（或任何有 canvas + screens[].dest 的東西）全部乘比例；其他欄位照舊。cabinet（箱體 = 真實 LED 箱體的點數）一起縮，格線才還是一片一片真的箱體 */
export function scalePlan<P extends { canvas: SizeLike; screens: readonly { dest: RectLike; cabinet?: { w: number; h: number } | null }[]; cabinet?: { w: number; h: number } | null }>(
  plan: P, s: WorkScale | null | undefined,
): P {
  if (isFullScale(s)) return plan;
  const cab = (c: { w: number; h: number } | null | undefined) => (c ? { w: scaleValue(c.w, s), h: scaleValue(c.h, s) } : c);
  return {
    ...plan,
    canvas: scaleCanvasSize(plan.canvas, s),
    // 沒設箱體的版型原本用 128 × 128（ledCabinet.ts 的預設）：縮小後要明講，不然格線一格會變成兩片箱體
    cabinet: cab(plan.cabinet ?? DEFAULT_CABINET_POINTS),
    screens: plan.screens.map(sc => ({ ...sc, dest: scaleRect(sc.dest, s), ...(sc.cabinet ? { cabinet: cab(sc.cabinet) } : {}) })),
  } as P;
}

// ───────────────────────── 檢查 ─────────────────────────

export interface WorkScaleInput {
  /** 原尺寸畫布 */
  canvas: SizeLike;
  /** 每一塊 LED 的矩形（原尺寸；跨畫布邊緣、不規則形狀用外框）。沒有 LED 資料 = 空陣列，只檢查畫布 */
  panels: readonly { name: string; rect: RectLike }[];
  /** 每個自己排的版型：畫布和每塊螢幕在版型裡的位置大小（原尺寸） */
  templates?: readonly { name: string; canvas: SizeLike; screens: readonly { name: string; dest: RectLike }[] }[];
}

export type ScaleField = 'width' | 'height' | 'x' | 'y' | 'w' | 'h';

export interface ScaleProblem {
  /** 哪一個：'畫布' / '主屏左' / '版型「舞臺中」的畫布' / '版型「舞臺中」的「主屏」' */
  where: string;
  field: ScaleField;
  /** 原本的值 */
  from: number;
  /** 縮完會變多少（可能是小數） */
  to: number;
  reason: 'odd' | 'fraction' | 'small';
  text: string;
}

const FIELD_LABEL: Record<ScaleField, string> = { width: '寬', height: '高', x: 'x', y: 'y', w: 'w', h: 'h' };

/** 數字給人看：整數不帶小數，小數最多 3 位 */
export function formatScaled(n: number): string {
  return Number.isInteger(n) ? String(n) : String(Math.round(n * 1000) / 1000);
}

function problemOf(where: string, field: ScaleField, from: number, s: WorkScale): ScaleProblem | null {
  const to = scaleValue(from, s);
  if (!Number.isInteger(to)) {
    return { where, field, from, to, reason: 'fraction', text: `${where}的 ${FIELD_LABEL[field]} 會變 ${formatScaled(to)}（不是整數）` };
  }
  if (to % 2 !== 0) {
    return { where, field, from, to, reason: 'odd', text: `${where}的 ${FIELD_LABEL[field]} 會變 ${to}（單數）` };
  }
  return null;
}

function canvasProblems(where: string, c: SizeLike, s: WorkScale, out: ScaleProblem[], checkSmall: boolean) {
  for (const f of ['width', 'height'] as const) {
    const p = problemOf(where, f, c[f], s);
    if (p) out.push(p);
    else if (checkSmall && scaleValue(c[f], s) < MIN_WORK_SIDE) {
      const to = scaleValue(c[f], s);
      out.push({ where, field: f, from: c[f], to, reason: 'small', text: `${where}的 ${FIELD_LABEL[f]} 會變 ${to}（太小，最小 ${MIN_WORK_SIDE}）` });
    }
  }
}

function rectProblems(where: string, r: RectLike, s: WorkScale, out: ScaleProblem[]) {
  for (const f of ['x', 'y', 'w', 'h'] as const) {
    const p = problemOf(where, f, r[f], s);
    if (p) out.push(p);
  }
}

/** 這個比例縮下去，哪些值不是整數雙數（空 = 可以選）。原尺寸一律可以選 */
export function workScaleProblems(input: WorkScaleInput, s: WorkScale | null | undefined): ScaleProblem[] {
  if (isFullScale(s)) return [];
  const sc = s as WorkScale;
  const out: ScaleProblem[] = [];
  canvasProblems('畫布', input.canvas, sc, out, true);
  for (const p of input.panels) rectProblems(p.name || 'LED', p.rect, sc, out);
  for (const t of input.templates ?? []) {
    canvasProblems(`版型「${t.name}」的畫布`, t.canvas, sc, out, true);
    for (const scr of t.screens) rectProblems(`版型「${t.name}」的「${scr.name}」`, scr.dest, sc, out);
  }
  return out;
}

export interface WorkScaleOption {
  scale: WorkScale;
  /** '3/4'；原尺寸 = '原尺寸' */
  text: string;
  /** 縮完的畫布大小（不能選時可能是小數，只給畫面參考） */
  canvas: SizeLike;
  ok: boolean;
  problems: ScaleProblem[];
  /** 不能選的原因（一句）：「1/4 不行：主屏左的 x 會變 1375（單數）」，還有別的就加「…等 N 處」；可以選 = null */
  reason: string | null;
}

export function workScaleOption(input: WorkScaleInput, s: WorkScale): WorkScaleOption {
  const problems = workScaleProblems(input, s);
  const first = problems[0];
  const more = problems.length > 1 ? `…等 ${problems.length} 處` : '';
  return {
    scale: s,
    text: workScaleText(s),
    canvas: scaleCanvasSize(input.canvas, s),
    ok: problems.length === 0,
    problems,
    reason: first ? `${workScaleText(s)} 不行：${first.text}${more}` : null,
  };
}

/** 每個常見比例能不能選、不能選的原因 */
export function workScaleOptions(input: WorkScaleInput): WorkScaleOption[] {
  return WORK_SCALE_CANDIDATES.map(c => workScaleOption(input, c));
}

/** 清單最多幾個（畫布很方正時可能有上萬個，太多就平均挑，大小還是涵蓋頭尾） */
export const WORK_SCALE_LIST_MAX = 500;

export interface WorkScalePickList {
  /** 第一個 = 原尺寸，後面是所有可以選的，由大到小 */
  options: WorkScaleOption[];
  /** 常見比例裡不能選的，說明卡在哪裡 */
  blocked: WorkScaleOption[];
  /** 可以選的太多，只平均列出一部分 */
  thinned: boolean;
}

/**
 * 所有「剛好等比、全部雙數」的尺寸。畫布要剛好等比，比例一定是 n / g（g = 寬高的最大公因數）；
 * 每個值 v 要縮完是雙數，n 就要是 2g / gcd(2g, v) 的倍數，所以先算出共同的倍數再逐一確認（最後還是以 workScaleProblems 為準）。
 */
export function workScalePickList(input: WorkScaleInput): WorkScalePickList {
  const full = workScaleOption(input, { num: 1, den: 1 });
  const blocked = WORK_SCALE_CANDIDATES.filter(c => !isFullScale(c)).map(c => workScaleOption(input, c)).filter(o => !o.ok);
  const { width, height } = input.canvas;
  const g = gcd(width, height);
  const values = [width, height];
  for (const p of input.panels) values.push(p.rect.x, p.rect.y, p.rect.w, p.rect.h);
  for (const t of input.templates ?? []) {
    values.push(t.canvas.width, t.canvas.height);
    for (const scr of t.screens) values.push(scr.dest.x, scr.dest.y, scr.dest.w, scr.dest.h);
  }
  let step = 1;
  for (const v of values) {
    const r = Math.round(v);
    if (r === 0 || Math.abs(v - r) > EPS) continue;   // 不是整數的值交給下面逐一確認
    const need = (2 * g) / gcd(2 * g, r);
    step = (step * need) / gcd(step, need);
    if (step >= g) return { options: [full], blocked, thinned: false };
  }
  const count = Math.floor((g - 1) / step);
  const thinned = count > WORK_SCALE_LIST_MAX;
  const every = thinned ? step * Math.ceil(count / WORK_SCALE_LIST_MAX) : step;
  const options = [full];
  for (let n = Math.floor((g - 1) / every) * every; n >= every; n -= every) {
    const d = gcd(n, g);
    const o = workScaleOption(input, { num: n / d, den: g / d });
    if (o.ok) options.push(o);
  }
  return { options, blocked, thinned };
}

/** 原尺寸裡本來就是單數（或不是整數）的值：原尺寸仍可用，只是要標出來提醒 */
export function oddOriginals(input: WorkScaleInput): ScaleProblem[] {
  const out: ScaleProblem[] = [];
  const check = (where: string, field: ScaleField, v: number) => {
    if (isEvenInt(v)) return;
    const reason = Number.isInteger(v) ? 'odd' as const : 'fraction' as const;
    out.push({ where, field, from: v, to: v, reason, text: `${where}的 ${FIELD_LABEL[field]} 是 ${formatScaled(v)}（${reason === 'odd' ? '單數' : '不是整數'}）` });
  };
  check('畫布', 'width', input.canvas.width);
  check('畫布', 'height', input.canvas.height);
  for (const p of input.panels) for (const f of ['x', 'y', 'w', 'h'] as const) check(p.name || 'LED', f, p.rect[f]);
  for (const t of input.templates ?? []) {
    check(`版型「${t.name}」的畫布`, 'width', t.canvas.width);
    check(`版型「${t.name}」的畫布`, 'height', t.canvas.height);
    for (const scr of t.screens) for (const f of ['x', 'y', 'w', 'h'] as const) check(`版型「${t.name}」的「${scr.name}」`, f, scr.dest[f]);
  }
  return out;
}

/** 後台用：只有畫布 + 版型能檢查（LED 的 UV 矩形後台拿不到，由前端檢查）。回傳說明，可以 = null */
export function workScaleServerCheck(input: Pick<WorkScaleInput, 'canvas' | 'templates'>, s: WorkScale | null | undefined): string | null {
  const problems = workScaleProblems({ canvas: input.canvas, panels: [], templates: input.templates }, s);
  if (problems.length === 0) return null;
  const more = problems.length > 1 ? `…等 ${problems.length} 處` : '';
  return `${workScaleText(s)} 不行：${problems[0].text}${more}`;
}
